import { css } from "@emotion/css";
import { Callout, Checkbox } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { Permission, PriorityMode } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { PriorityDeploymentContextualHelp } from "~/components/ContextualHelp/ContextualHelpSnippets";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import LicensedFeatureVisibility from "~/components/LicensedFeature/LicensedFeature";
import { useIsLicensedFeatureEnabled } from "~/components/LicensedFeature/useIsLicensedFeatureEnabled";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { RequestTrial } from "~/components/RequestTrial/RequestTrial";
import ExpandableFormSection from "../../../../../components/form/Sections/ExpandableFormSection";
import Summary from "../../../../../components/form/Sections/Summary";
interface PriorityOptionsProps {
    priority: PriorityMode;
    context: "deployment" | "runbook";
    onChange(value: boolean): void;
}
const PriorityOptionsDeprecated = (props: PriorityOptionsProps) => {
    const hasPermissionToChangePriority = isAllowed({ permission: [Permission.TaskPrioritize] });
    const getHelp = () => (props.context === "deployment" ? "Choose whether to deploy with priority" : "Choose whether to run with priority");
    const buildSummary = (priorityMode: PriorityMode) => (priorityMode === PriorityMode.On ? Summary.summary(`${props.context === "deployment" ? "Deploy" : "Run"} with priority`) : Summary.default("No priority"));
    return (<ExpandableFormSection title="Priority" errorKey="priority" help={getHelp()} summary={buildSummary(props.priority)}>
            {!hasPermissionToChangePriority && (<Callout title="Missing permission" type={"information"}>
                    You require the TaskPrioritize permission to change the priority of this {props.context === "deployment" ? "deployment" : "runbook run"}.
                </Callout>)}
            <div>
                <Checkbox disabled={!hasPermissionToChangePriority} label="Priority" value={props.priority === PriorityMode.On} onChange={props.onChange}/>
            </div>
        </ExpandableFormSection>);
};
const PriorityOptionsNew = (props: PriorityOptionsProps) => {
    const hasPermissionToChangePriority = isAllowed({ permission: [Permission.TaskPrioritize] });
    const isLifecyclePhaseFeatureEnabled = isFeatureToggleEnabled("LifecyclePhasePriorityDeploymentFeatureToggle");
    const isLicensedFeatureEnabled = useIsLicensedFeatureEnabled("PriorityTask");
    const isLicensed = isLifecyclePhaseFeatureEnabled || isLicensedFeatureEnabled;
    const canChange = hasPermissionToChangePriority && isLicensed;
    const getHelp = () => (props.context === "deployment" ? "Choose whether to prioritize this deployment" : "Choose whether to prioritize this runbook");
    const checkboxLabel = props.context === "deployment" ? "Prioritize this deployment" : "Prioritize this runbook";
    const buildSummary = (priorityMode: PriorityMode) => (priorityMode === PriorityMode.On ? Summary.summary(`${props.context === "deployment" ? "Deploy" : "Run"} with priority`) : Summary.default("No priority"));
    return (<ExpandableFormSection title="Priority" errorKey="priority" help={getHelp()} summary={buildSummary(props.priority)}>
            {!hasPermissionToChangePriority && (<Callout title="Missing permission" type={"information"}>
                    You require the TaskPrioritize permission to change the priority of this {props.context === "deployment" ? "deployment" : "runbook run"}.
                </Callout>)}
            {hasPermissionToChangePriority && !isLicensed && (<RequestTrial type="inline-message" featureName={"Priority Deployments"} onRequestTrial={async (repository) => {
                await repository.Deployments.requestPriorityDeploymentsTrial();
            }} content="Priority is available on the Enterprise tier." dialogTitle="Request a free trial of Priority"/>)}
            <div className={styles.checkboxContainer}>
                <Checkbox disabled={!canChange} label={checkboxLabel} value={props.priority === PriorityMode.On} onChange={props.onChange} noMargin={true}/> <PriorityDeploymentContextualHelp context={props.context}/>
            </div>
        </ExpandableFormSection>);
};
const PriorityOptions = (props: PriorityOptionsProps) => {
    const isFreeTierEnabled = isFeatureToggleEnabled("free-tier");
    if (isFreeTierEnabled) {
        return <PriorityOptionsNew {...props}/>;
    }
    else {
        return (<LicensedFeatureVisibility feature={"PriorityTask"}>
                <PriorityOptionsDeprecated {...props}/>
            </LicensedFeatureVisibility>);
    }
};
const styles = {
    checkboxContainer: css({
        // align checkbox and contextual help on one line
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: space[8],
    }),
};
export default PriorityOptions;
